import { CARD_W, GAP } from "./OrgChart.styles";

/**
 * @fileoverview Pure geometry for the org chart's dashed "also reports to"
 * cross-links. Given measured card rectangles in untransformed tree space, it
 * routes each link subway-style: out of the source card's side, down a corridor
 * chosen to clear every card, along its own lane in a gutter reserved beneath the
 * tree, then up into the target card's side. No DOM access — OrgChart measures,
 * this decides.
 */

/* cross-link routing, in untransformed tree-space px */
const LANE_TOP = 20; /* gap between the tree's bottom and the first lane */
const LANE_STEP = 13; /* vertical distance between two lanes */
const LANE_TAIL = 10; /* breathing room under the last lane */
const CLEAR_MARGIN = 4; /* how close a corridor may run to a card */
const CORRIDOR_GAP = GAP / 2; /* first corridor offset — the middle of the column gutter */
const CORRIDOR_STEP = GAP + CARD_W; /* next candidates clear whole neighbouring columns */
const CORRIDOR_TRIES = 6;

const round1 = (v) => Math.round(v * 10) / 10;

/** true when a vertical run at `x` spanning [y1, y2] would touch `r`. */
function hitsRect(x, y1, y2, r) {
  return (
    x >= r.x - CLEAR_MARGIN &&
    x <= r.x + r.w + CLEAR_MARGIN &&
    y2 >= r.y - CLEAR_MARGIN &&
    y1 <= r.y + r.h + CLEAR_MARGIN
  );
}

/**
 * A vertical corridor beside `card` on side `dir` (-1 left, +1 right) that runs
 * from the card down to the gutter without crossing any card. Steps outward
 * until it finds clear space; falls back to the closest offset.
 */
function corridorX(card, dir, bottom, rects) {
  const edge = dir < 0 ? card.x : card.x + card.w;
  const y1 = card.y + card.h / 2;
  let best = null;

  for (let i = 0; i < CORRIDOR_TRIES; i++) {
    const x = edge + dir * (CORRIDOR_GAP + i * CORRIDOR_STEP);
    const hits = rects.reduce((n, r) => (hitsRect(x, y1, bottom, r) ? n + 1 : n), 0);
    if (hits === 0) return x;
    if (!best || hits < best.hits) best = { x, hits };
  }
  return best.x;
}

/** orthogonal polyline through `points`, with the corners rounded. */
export function roundedPath(points, radius = 7) {
  const pts = points.filter(
    (p, i) => i === 0 || Math.abs(p.x - points[i - 1].x) > 0.5 || Math.abs(p.y - points[i - 1].y) > 0.5,
  );
  if (pts.length < 2) return "";

  const at = (p) => `${round1(p.x)} ${round1(p.y)}`;
  const towards = (from, to, dist) => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const len = Math.hypot(dx, dy) || 1;
    const k = Math.min(dist, len) / len;
    return { x: from.x + dx * k, y: from.y + dy * k };
  };

  const out = [`M ${at(pts[0])}`];
  for (let i = 1; i < pts.length - 1; i++) {
    const prev = pts[i - 1];
    const corner = pts[i];
    const next = pts[i + 1];
    const r = Math.min(
      radius,
      Math.hypot(corner.x - prev.x, corner.y - prev.y) / 2,
      Math.hypot(next.x - corner.x, next.y - corner.y) / 2,
    );
    out.push(`L ${at(towards(corner, prev, r))}`, `Q ${at(corner)} ${at(towards(corner, next, r))}`);
  }
  out.push(`L ${at(pts[pts.length - 1])}`);
  return out.join(" ");
}

/**
 * Route every cross-link whose endpoints are currently on screen. Each link
 * leaves its parent card sideways, drops down a clear corridor, crosses the
 * gutter under the tree in a lane of its own, and rises into the target card's
 * side. Lanes are allocated by greedy interval packing, so two links only share
 * a lane when their horizontal spans do not overlap.
 *
 * @returns {{ items: {key: string, label: string, d: string}[], gutter: number }}
 */
export function routeCrossLinks(links, rectOf, rects, treeHeight) {
  const pending = links
    .map((l) => {
      const from = rectOf(l.from);
      const to = rectOf(l.to);
      return from && to ? { ...l, from, to } : null;
    })
    .filter(Boolean);
  if (pending.length === 0) return { items: [], gutter: 0 };

  const bottom = treeHeight + LANE_TOP;
  const planned = pending.map((l) => {
    const goesRight = l.to.x + l.to.w / 2 >= l.from.x + l.from.w / 2;
    const exit = { x: goesRight ? l.from.x + l.from.w : l.from.x, y: l.from.y + l.from.h / 2 };
    const enter = { x: goesRight ? l.to.x : l.to.x + l.to.w, y: l.to.y + l.to.h / 2 };
    return {
      ...l,
      exit,
      enter,
      xs: corridorX(l.from, goesRight ? 1 : -1, bottom, rects),
      xt: corridorX(l.to, goesRight ? -1 : 1, bottom, rects),
    };
  });

  const lanes = [];
  const items = planned
    .slice()
    .sort((a, b) => Math.min(a.xs, a.xt) - Math.min(b.xs, b.xt))
    .map((l) => {
      const span = [Math.min(l.xs, l.xt), Math.max(l.xs, l.xt)];
      let lane = lanes.findIndex((taken) => taken.every(([s, e]) => span[1] < s || span[0] > e));
      if (lane === -1) lane = lanes.push([]) - 1;
      lanes[lane].push(span);

      const y = treeHeight + LANE_TOP + lane * LANE_STEP;
      return {
        key: l.key,
        label: l.label,
        d: roundedPath([
          l.exit,
          { x: l.xs, y: l.exit.y },
          { x: l.xs, y },
          { x: l.xt, y },
          { x: l.xt, y: l.enter.y },
          l.enter,
        ]),
      };
    });

  return { items, gutter: LANE_TOP + lanes.length * LANE_STEP + LANE_TAIL };
}
